import { ArrowDropDown } from "@mui/icons-material";
import { Box, MenuItem } from "@mui/material";
import SimpleMenu from "atoms/SimpleMenu";
import { ExtendedDataProvider } from "misc/dataProvider.types";
import { manageEmailingSenders } from "misc/permissionHelpers";
import { useEffect, useState } from "react";
import { SaveButton, Toolbar, useCreate, useDataProvider, useNotify, useTranslate } from "react-admin";
import { useFormContext } from "react-hook-form";

import { EdmButton } from "../../atoms";
import ScheduledEmailDialog from "./ScheduledEmailDialog";
import { EmailToolbarPropsType, SendEmailFormDataType } from "./SendEmailDialog.types";

type ExceptionType = {
  status: number;
  message: string;
  body?: { error?: string };
};

export default function EmailToolbar(props: EmailToolbarPropsType) {
  const { onBackClick, event, setPreviewContent, setShowPreview, showPreview, ...rest } = props;

  const notify = useNotify();
  const translate = useTranslate();
  const { getValues, setValue, trigger } = useFormContext<SendEmailFormDataType>();
  const dataProvider = useDataProvider<ExtendedDataProvider>();
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [formIsInvalid, setFormIsInvalid] = useState(true);
  const [create] = useCreate();

  const values = getValues();

  useEffect(() => {
    const validateForm = async () => {
      return await trigger();
    };

    if ((!manageEmailingSenders() || values.sender) && values.template) {
      validateForm().then((isValid) => setFormIsInvalid(!isValid));
    } else {
      setFormIsInvalid(true);
    }
  }, [trigger, values.sender, values.template, values.subject]);

  const setScheduledAt = (scheduled_at: string) => {
    setValue("scheduled_at", scheduled_at);
  };

  const getPreview = async () => {
    const { template, recipients } = getValues();
    trigger();

    if (!template) {
      notify("emailing.no_template");
      return false;
    }

    try {
      const res = await dataProvider.getEmailPreview({
        ...getValues(),
        recipients: recipients.slice(0, 1),
        scheduled_at: undefined,
      });
      setPreviewContent(res);
      setShowPreview(true);
    } catch (e) {
      const err = e as ExceptionType;
      notify("emailing.preview_error", {
        type: "error",
        messageArgs: { error: err.body?.error ? err.body?.error : err.message },
      });
    }
  };

  const handleTestMailClick = async () => {
    const data = {
      ...getValues(),
      is_test: true,
      scheduled_at: undefined,
    };

    try {
      await create("email_jobs", { data });
      notify("emailing.test_success");
    } catch {
      notify("emailing.test_error");
    }
  };

  return (
    <Toolbar {...rest} sx={{ backgroundColor: "transparent" }}>
      <Box sx={{ width: "100%", display: "flex", justifyContent: "end", alignItems: "center" }}>
        {onBackClick && !showPreview && (
          <EdmButton color="info" label="ra.action.back" onClick={onBackClick} size="medium" sx={{ marginRight: 1 }} />
        )}

        {showPreview && (
          <EdmButton
            onClick={() => setShowPreview(false)}
            label="ra.action.back"
            size="medium"
            sx={{ marginRight: 1 }}
          />
        )}

        <Box sx={{ textAlign: "right" }}>
          {!showPreview && (
            <EdmButton onClick={getPreview} label="emailing.preview" size="medium" sx={{ marginRight: 1 }} />
          )}

          <SaveButton
            label="emailing.send"
            icon={<></>}
            disabled={formIsInvalid}
            alwaysEnable={!formIsInvalid}
            size="medium"
          />

          <SimpleMenu
            button={
              <EdmButton
                label=""
                sx={{ p: 1, minWidth: "30px", ml: 1, "& .MuiButton-startIcon": { mr: 0 } }}
                disabled={formIsInvalid}
                color="primary"
              >
                <ArrowDropDown />
              </EdmButton>
            }
          >
            {(closeMenu) => [
              <MenuItem
                key="scheduled_dispatch"
                onClick={() => {
                  setShowScheduleDialog(true);
                  closeMenu();
                }}
              >
                {translate("emailing.scheduled_dispatch")}
              </MenuItem>,
              <MenuItem
                key="sendtest"
                onClick={() => {
                  handleTestMailClick();
                  closeMenu();
                }}
              >
                {translate("emailing.sendtest")}
              </MenuItem>,
            ]}
          </SimpleMenu>
        </Box>
      </Box>

      <ScheduledEmailDialog
        setScheduledAt={setScheduledAt}
        isOpen={showScheduleDialog}
        closeDialog={() => setShowScheduleDialog(false)}
        event={event}
        emailFormIsValid={!formIsInvalid}
      />
    </Toolbar>
  );
}
